import { Injectable } from '@angular/core';
import { Observable, finalize, map, of } from 'rxjs';
import { ApiService } from '../../../services/api.service';
import {
  MembershipRewashEligibilityState,
  PrepaidRewashEligibilityState,
  OrderRequestHistoryInsightCacheEntry,
  OrderRequestTodayBannerState,
  RewashEligibilityState,
} from '../models/notification-order-request.models';
import { cloneOrderRequestTodayBanner } from '../utils/notification-order-request-history.util';

export type OrderRequestHistoryFetchSkipReason = 'in-flight' | 'cooldown' | 'stale';

export interface OrderRequestHistoryFetchResult {
  skipped: boolean;
  skipReason?: OrderRequestHistoryFetchSkipReason;
  response?: any;
}

/** Optional filters for vehicle history (Active subscription omit-opening-wash, etc.). */
export interface OrderRequestHistoryFetchFilters {
  historyTypes?: string | string[];
  excludeSubscriptionWashId?: string | null;
}

export const ORDER_REQUEST_HISTORY_PAGE_SIZE_OPTIONS = Object.freeze([10, 25, 50]);

@Injectable({ providedIn: 'root' })
export class NotificationOrderRequestHistoryService {
  private _pageLimit = 10;
  private readonly requestCooldownMs = 1500;
  private readonly requestInFlight = new Set<string>();
  private readonly lastRequestAt: Record<string, number> = {};
  private readonly insightCache: Record<string, OrderRequestHistoryInsightCacheEntry> = {};
  /** Bumped on invalidate so in-flight responses from a prior generation are ignored. */
  private readonly vehicleFetchGeneration: Record<string, number> = {};

  constructor(private readonly apiService: ApiService) {}

  get pageLimit(): number {
    return this._pageLimit;
  }

  setPageLimit(limit: number): void {
    const parsed = Number(limit);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return;
    }
    this._pageLimit = Math.floor(parsed);
  }

  buildContextKey(
    customerId: string,
    vehicleId: string,
    filters: OrderRequestHistoryFetchFilters = {}
  ): string | null {
    const cid = (customerId || '').trim();
    const vid = (vehicleId || '').trim();
    if (!cid || !vid) {
      return null;
    }
    const types = filters.historyTypes
      ? Array.isArray(filters.historyTypes)
        ? filters.historyTypes.join(',')
        : String(filters.historyTypes)
      : '';
    const exclude = String(filters.excludeSubscriptionWashId ?? '').trim();
    if (!types && !exclude) {
      return `${cid}::${vid}`;
    }
    return `${cid}::${vid}::${types}::${exclude}`;
  }

  peekFetchSkip(
    vehicleId: string,
    page: number,
    buildInsight: boolean,
    filters: OrderRequestHistoryFetchFilters = {}
  ): OrderRequestHistoryFetchSkipReason | null {
    const requestKey = this.buildRequestKey(vehicleId, page, buildInsight, filters);
    const now = Date.now();
    if (this.requestInFlight.has(requestKey)) {
      return 'in-flight';
    }
    const lastAt = this.lastRequestAt[requestKey] ?? 0;
    if (now - lastAt < this.requestCooldownMs) {
      return 'cooldown';
    }
    return null;
  }

  fetchVehicleOrderHistoryPage(
    vehicleId: string,
    page: number,
    buildInsight: boolean,
    filters: OrderRequestHistoryFetchFilters = {}
  ): Observable<OrderRequestHistoryFetchResult> {
    const vid = (vehicleId || '').trim();
    if (!vid) {
      return of({ skipped: true });
    }

    const skipReason = this.peekFetchSkip(vid, page, buildInsight, filters);
    if (skipReason) {
      return of({ skipped: true, skipReason });
    }

    const requestKey = this.buildRequestKey(vid, page, buildInsight, filters);
    const fetchGeneration = this.vehicleFetchGeneration[vid] ?? 0;
    this.requestInFlight.add(requestKey);
    this.lastRequestAt[requestKey] = Date.now();

    const excludeId = String(filters.excludeSubscriptionWashId ?? '').trim();

    return this.apiService
      .getVehicleOrderHistory(vid, {
        page,
        limit: this.pageLimit,
        include_rewash_eligibility: buildInsight,
        ...(filters.historyTypes ? { history_types: filters.historyTypes } : {}),
        ...(excludeId ? { exclude_subscription_wash_id: excludeId } : {}),
      })
      .pipe(
        finalize(() => {
          this.requestInFlight.delete(requestKey);
        }),
        map(response => {
          if ((this.vehicleFetchGeneration[vid] ?? 0) !== fetchGeneration) {
            return { skipped: true, skipReason: 'stale' as OrderRequestHistoryFetchSkipReason };
          }
          return { skipped: false, response };
        })
      );
  }

  saveInsightCache(
    contextKey: string,
    banner: OrderRequestTodayBannerState | null,
    suggestion: string,
    rewashEligibility: RewashEligibilityState | null = null,
    membershipRewashEligibility: MembershipRewashEligibilityState | null = null,
    prepaidRewashEligibility: PrepaidRewashEligibilityState | null = null
  ): void {
    if (!banner) {
      delete this.insightCache[contextKey];
      return;
    }
    this.insightCache[contextKey] = {
      banner: cloneOrderRequestTodayBanner(banner),
      suggestion,
      rewashEligibility: rewashEligibility ? { ...rewashEligibility } : null,
      membershipRewashEligibility: membershipRewashEligibility
        ? {
            eligible: membershipRewashEligibility.eligible,
            candidates: membershipRewashEligibility.candidates.map(c => ({ ...c })),
          }
        : null,
      prepaidRewashEligibility: prepaidRewashEligibility
        ? {
            eligible: prepaidRewashEligibility.eligible,
            candidates: prepaidRewashEligibility.candidates.map(c => ({ ...c })),
          }
        : null,
    };
  }

  restoreInsightCache(contextKey: string): OrderRequestHistoryInsightCacheEntry | null {
    const cached = this.insightCache[contextKey];
    if (!cached) {
      return null;
    }
    return {
      banner: cloneOrderRequestTodayBanner(cached.banner),
      suggestion: cached.suggestion,
      rewashEligibility: cached.rewashEligibility ? { ...cached.rewashEligibility } : null,
      membershipRewashEligibility: cached.membershipRewashEligibility
        ? {
            eligible: cached.membershipRewashEligibility.eligible,
            candidates: cached.membershipRewashEligibility.candidates.map(c => ({ ...c })),
          }
        : null,
      prepaidRewashEligibility: cached.prepaidRewashEligibility
        ? {
            eligible: cached.prepaidRewashEligibility.eligible,
            candidates: cached.prepaidRewashEligibility.candidates.map(c => ({ ...c })),
          }
        : null,
    };
  }

  clearAll(): void {
    this.requestInFlight.clear();
    Object.keys(this.lastRequestAt).forEach(k => delete this.lastRequestAt[k]);
    Object.keys(this.insightCache).forEach(k => delete this.insightCache[k]);
    this._pageLimit = 10;
  }

  /** Allows an immediate refetch after plate / vehicle context changes. */
  invalidateForVehicle(vehicleId: string): void {
    const vid = (vehicleId || '').trim();
    if (!vid) {
      return;
    }
    this.vehicleFetchGeneration[vid] = (this.vehicleFetchGeneration[vid] ?? 0) + 1;
    const prefix = `${vid}::`;
    for (const key of [...this.requestInFlight]) {
      if (key.startsWith(prefix)) {
        this.requestInFlight.delete(key);
      }
    }
    for (const key of Object.keys(this.lastRequestAt)) {
      if (key.startsWith(prefix)) {
        delete this.lastRequestAt[key];
      }
    }
    for (const key of Object.keys(this.insightCache)) {
      // Keys are `customerId::vehicleId` or `customerId::vehicleId::types::exclude`.
      const parts = key.split('::');
      if (parts.length >= 2 && parts[1] === vid) {
        delete this.insightCache[key];
      }
    }
  }

  private buildRequestKey(
    vehicleId: string,
    page: number,
    buildInsight: boolean,
    filters: OrderRequestHistoryFetchFilters = {}
  ): string {
    const types = filters.historyTypes
      ? Array.isArray(filters.historyTypes)
        ? filters.historyTypes.join(',')
        : String(filters.historyTypes)
      : '';
    const exclude = String(filters.excludeSubscriptionWashId ?? '').trim();
    return `${vehicleId}::${page}::${this.pageLimit}::${buildInsight ? 'insight' : 'page'}::${types}::${exclude}`;
  }
}
